/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Validators } from '@angular/forms';
import {
  AppFormGroup,
  ApplicationStateHolder,
  BaseControlSchemaConfig,
  ControlSchemaPackedValue,
  ControlSchemaType,
  FormGroupSchema,
  FormGroupSchemaConfig,
} from '@gh/common-shared';
import { denull } from '@gh/core-util';
import { get, isNil, mapValues, values } from 'lodash';
import { AbstractBuilderControlSchema } from './abstract-builder-control-schema';
import { ensureControlSchema } from './builder-form-control-schema';
import { diffFormGroup } from './form-diffs';

export function ensureGroupSchema(stateHolder: ApplicationStateHolder, child: any): AbstractBuilderControlSchema {
  if (child instanceof BuilderFormGroupSchema) {
    return child;
  } else {
    const schema = new BuilderFormGroupSchema(stateHolder);
    schema.update(child || {});
    return schema;
  }
}

export class BuilderFormGroupSchema extends AbstractBuilderControlSchema implements FormGroupSchema {
  schemaType = ControlSchemaType.Group;

  children: Hash<AbstractBuilderControlSchema> = {};

  constructor(stateHolder: ApplicationStateHolder) {
    super(stateHolder);
  }

  update(config: FormGroupSchemaConfig): void {
    super.update(config);

    this.children = isNil(config.children) ? this.children : <any>mapValues(
      config.children,
      (child: any, key) =>
        this.updateOrCreateItem(this.children[key], child));
  }

  applyTo(control: AppFormGroup, packedValue?: ControlSchemaPackedValue): void {
    diffFormGroup(this, control, packedValue);

    super.applyTo(control);
  }

  build(value?: any): AppFormGroup {
    const group = new AppFormGroup(
      this,
      mapValues(this.children, (schema: any, name: string) =>
        schema.build(get(value, name)),
      ),
      denull(Validators.compose(this.buildValidators())),
      denull(Validators.composeAsync(this.asyncValidators)),
    );

    group.required = this.isRequired();
    group.visible = this.isVisible();
    group.privileged = this.isPrivileged();
    group.lastValidators = this.validators;
    group.lastAsyncValidators = this.asyncValidators;

    if (!this.isEnabled()) {
      group.disable();
    }

    return group;
  }

  private updateOrCreateItem(schema: AbstractBuilderControlSchema,
                             schemaConfig?: BaseControlSchemaConfig): AbstractBuilderControlSchema {
    if (schema) {
      schema.update(schemaConfig || {});
      return schema;
    } else {
      return ensureControlSchema(this.stateHolder, schemaConfig);
    }
  }
}
